import React from 'react';
import styled from '@emotion/styled';
import { Badge, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { USER_PROFILE } from '../../../constants/define/translation';
import { CommonColumn } from '../shared-column';
import { ActionsColumn } from './columns';
import { FORMAT_DATETIME_US } from '../../../constants/common';

const roleName = [
  { id: 1, name: 'USER' },
  { id: 2, name: 'ADMIN' },
  { id: 3, name: 'SUPER ADMIN' },
  { id: 4, name: 'CUSTOMER SERVICE' },
  { id: 5, name: 'TTB AGENT' },
  { id: 6, name: 'TTB Admin' },
];

export const MyTMPlusTable = (prop: any) => {
  const googleMap = 'https://www.google.com/maps/place';
  const whatMyIp = 'https://whatismyipaddress.com/';
  const { userProfile, setRefresh, setIsLoading } = prop;
  const { userPermission, userBlackList } = userProfile;
  const { t } = useTranslation(USER_PROFILE);
  const { Column } = Table;

  const formatDate = (datetime: string) => {
    if (!datetime) return '';
    return moment(datetime).format(FORMAT_DATETIME_US);
  };

  const formatGps = (user: any) => {
    const lastLatitude = user.lastLatitude || null;
    const lastLongitude = user.lastLongitude || null;
    return (
      <div>
        {!!lastLatitude && lastLatitude}
        {!!lastLatitude && !!lastLongitude && ', '}
        {!!lastLongitude && lastLongitude}
      </div>
    );
  };

  const getUrlGps = (lat: string, long: string) => {
    if (!lat && !long) {
      return null;
    }
    return `${googleMap}/${lat}, ${long}`;
  };

  const getUrlIp = (ip: string) => {
    if (!ip) {
      return null;
    }
    return `${whatMyIp}/ip/${ip}`;
  };
  const records: any = [
    {
      description: 'Email',
      value: userProfile?.email,
      action: 'COPY',
    },
    {
      description: 'Active',
      value: (
        <Badge status={userProfile?.deletedAt ? 'error' : 'success'} text={userProfile?.deletedAt ? 'false' : 'true'} />
      ),
      action: 'TOGGLE_ACTIVE',
      userId: userProfile.id,
      deletedAt: userProfile?.deletedAt,
    },
    {
      description: 'TTB Blacklisted',
      value: (
        <Badge
          status={userBlackList && !userBlackList.deletedAt && userBlackList.id ? 'success' : 'error'}
          text={userBlackList && !userBlackList.deletedAt && userBlackList.id ? 'true' : 'false'}
        />
      ),
      action: 'TOGGLE_BLACKLIST',
      email: userProfile?.email,
      userBlackListId: (userBlackList && !userBlackList.deletedAt && userBlackList?.id) || null,
    },
    {
      description: 'Country',
      value: userProfile?.userCountry || null,
      action: 'EDIT',
      userId: userProfile.id,
      deletedAt: userProfile?.deletedAt,
    },
    {
      description: 'myTMSubscriptionLevel',
      value: userProfile?.myTMSubscriptionLevel,
    },
    {
      description: 'Subscription Start Date',
      value: formatDate(userPermission?.subscriptionStartDate),
    },
    {
      description: 'Subscription Expiration Date',
      value: formatDate(userPermission?.subscriptionExpirationDate),
    },
    {
      description: 'subscriptionService',
      value: userProfile?.subscriptionService,
    },
    {
      description: 'signUpByDevice',
      value: userProfile?.signUpByDevice,
    },
    {
      description: 'Advanced Round',
      value: (
        <Badge
          status={userPermission?.canPlayAdvancedRound ? 'success' : 'error'}
          text={userPermission?.canPlayAdvancedRound ? 'true' : 'false'}
        />
      ),
    },
    {
      description: 'Swing Coach',
      value: (
        <Badge
          status={userPermission?.canVirtualCoaching ? 'success' : 'error'}
          text={userPermission?.canVirtualCoaching ? 'true' : 'false'}
        />
      ),
    },
    {
      description: 'Try Then Buy',
      value: (
        <Badge
          status={userPermission?.canTryThenBuy ? 'success' : 'error'}
          text={userPermission?.canTryThenBuy ? 'true' : 'false'}
        />
      ),
    },
    {
      description: 'Member Shop',
      value: (
        <Badge
          status={userPermission?.canExclProductDrops ? 'success' : 'error'}
          text={userPermission?.canExclProductDrops ? 'true' : 'false'}
        />
      ),
    },
    {
      description: 'Tour Trash',
      value: (
        <Badge
          status={userPermission?.canWinTourTrash ? 'success' : 'error'}
          text={userPermission?.canWinTourTrash ? 'true' : 'false'}
        />
      ),
    },
    {
      description: 'USGA Handicap Index',
      value: null,
    },
    {
      description: 'USGA ID',
      value: null,
    },
    {
      description: 'Arccos User',
      value: (
        <Badge
          status={userProfile?.arccosUserId ? 'success' : 'error'}
          text={userProfile?.arccosUserId ? 'true' : 'false'}
        />
      ),
    },
    {
      description: 'Role',
      value: roleName.find(val => val.id === userProfile?.role)?.name,
    },
    {
      description: 'fcmToken',
      value: userProfile?.fcmToken,
      action: 'COPY',
    },
    {
      description: 'Last Login IP',
      value: userProfile?.lastIPAddress,
      url: getUrlIp(userProfile?.lastIPAddress),
      action: 'VIEW',
    },
    {
      description: 'Last Login GPS',
      value: formatGps(userProfile),
      url: getUrlGps(userProfile?.lastLatitude, userProfile?.lastLongitude),
      action: 'VIEW',
    },
    {
      description: 'Last Login Date',
      value: formatDate(userProfile?.lastActivatedAt),
    },
  ];
  const loading = false;

  return (
    <>
      <StyledTable dataSource={records} loading={loading} rowKey="id" pagination={false}>
        <Column
          title={t('table.title.description')}
          width={200}
          dataIndex="description"
          key="description"
          render={value => <CommonColumn value={value} />}
          align="center"
        />
        <Column
          title={t('table.title.value')}
          width={200}
          dataIndex="value"
          key="value"
          render={value => <CommonColumn value={value} />}
          align="center"
          className="fcmToken"
          ellipsis
        />
        <Column
          title={t('table.title.action')}
          width={200}
          dataIndex="action"
          key="action"
          render={(value, record) => (
            <ActionsColumn record={record} setRefresh={setRefresh} setIsLoading={setIsLoading} />
          )}
          align="center"
        />
      </StyledTable>
    </>
  );
};

const StyledTable = styled(Table)`
  .fcmToken > div {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
